import * as React from "react";
import {
  Autocomplete,
  AutocompleteMultiSelectSearchBox,
  Box,
  Button,
  Checkbox,
  focusRing,
  Grid2 as Grid,
  Paper,
  pxToRem,
  TextField,
  Typography,
} from "@platform/style";
import { fetchWithToken } from "@platform/core";
import {
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE,
  WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2,
  WORKITEM_BFF_API_BASE_USERS,
} from "../../platform-workitems";
import { reportError } from "../../utils/errors";
import { WorkGroup } from "../../models/WorkGroup";
import { TemplateSummary } from "../../models/TemplateSummary";
import { Resource } from "../../models/Resource";
import { WorkGroupRequest } from "../../models/WorkGroupRequest";
import { TemplateV2 } from "../../models/TemplateV2";

interface WorkGroupUpdateProps {
  workGrp?: WorkGroup;
  handleCancelClick: () => void;
  snackBarDetails?: (message: string, open: boolean) => void;
  onError: (message: string) => void;
  refetch: Function;
}

export function CreateWorkGroup({
  workGrp,
  handleCancelClick,
  snackBarDetails,
  onError,
  refetch,
}: Readonly<WorkGroupUpdateProps>) {
  const [description, setDescription] = React.useState<string>("");
  const [name, setName] = React.useState<string>("");
  const [templates, setTemplates] = React.useState<
    { key: string; name?: string; optionValue: string }[]
  >([]);
  const [resources, setResources] = React.useState<
    { key: string; name?: string; optionValue: string }[]
  >([]);
  const [resourcesInTenant, setResourcesInTenant] = React.useState<Resource[]>([]);
  const [templatesInTenant, setTemplatesInTenant] = React.useState<TemplateSummary[]>([]);
  const [nameInError, setNameInError] = React.useState<boolean>(false);
  const [descInError, setDescInError] = React.useState<boolean>(false);
  const [resourceInError, setResourceInError] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!workGrp) {
      setDescription("");
      setName("");
      setTemplates([]);
      setResources([]);
      setNameInError(false);
      setDescInError(false);
      setResourceInError(false);
    } else {
      setDescription(workGrp.Description);
      setName(workGrp.Name);
      setTemplates(getTemplateOptions(workGrp.Templates));
      setResources(getResourceOptions(workGrp.Resources));
    }

    (async () => {
      await fetchWithToken(`${WORKITEM_BFF_API_BASE_USERS}`, {
        method: "GET",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(JSON.stringify(response));
          }
          return response.json();
        })
        .then((result) => {
          setResourcesInTenant(result);
        })
        .catch((error) => {
          onError(`Unable to get users. Please try again.`);

          reportError(error.message);
        });
    })();

    (async () => {
      await fetchWithToken(`${WORK_MANAGEMENT_ADMIN_BFF_API_BASE_V2}/templates`, {
        method: "GET",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(JSON.stringify(response));
          }
          return response.json();
        })
        .then((result) => {
          const transformToTemplateSummaryList: TemplateSummary[] = [];
          result.filter((template: TemplateV2) => {
            transformToTemplateSummaryList.push({
              TemplateCode: template.TemplateCode,
              Title: template.Title,
            });
          });
          setTemplatesInTenant(transformToTemplateSummaryList);
        })
        .catch((error) => {
          onError(`Unable to get client templates. Please try again.`);

          reportError(error.message);
        });
    })();
  }, [workGrp]);

  function getTemplateOptions(templatesList: TemplateSummary[]) {
    const templateOptions: { key: string; optionValue: string }[] = [];

    templatesList?.length > 0 &&
      templatesList.filter((template) => {
        templateOptions.push({ key: template.TemplateCode, optionValue: template.TemplateCode });
      });

    return templateOptions;
  }

  function getResourceOptions(resourceList: Resource[]) {
    const resourceOptions: { key: string; optionValue: string }[] = [];

    resourceList?.length > 0 &&
      resourceList.forEach((resource) => {
        resourceOptions.push({
          key: resource.ResourceIdentifier,
          optionValue: resource.FullName,
        });
      });

    return resourceOptions.sort((a, b) => a.optionValue.localeCompare(b.optionValue));
  }

  function validateFields() {
    let errorCnt: number = 0;
    if (name.trim() == "") {
      setNameInError(true);
      errorCnt++;
    } else {
      setNameInError(false);
    }
    if (description.trim() == "") {
      setDescInError(true);
      errorCnt++;
    } else {
      setDescInError(false);
    }
    if (resources.length == 0) {
      setResourceInError(true);
      errorCnt++;
    } else {
      setResourceInError(false);
    }
    return errorCnt;
  }

  async function handleSaveClick() {
    const newWorkGroupRequest: WorkGroupRequest = {
      Name: name,
      Description: description,
      ResourceIdentifiers: resources.map((resIds) => resIds.key),
      TemplateCodes: templates.map((tempCodes) => tempCodes.key),
    };

    validateFields() == 0 &&
      (await fetchWithToken(
        !workGrp
          ? `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups`
          : `${WORK_MANAGEMENT_ADMIN_BFF_API_BASE}/work-groups/${workGrp.WorkGroupIdentifier}`,
        {
          method: !workGrp ? "POST" : "PUT",
          body: JSON.stringify(newWorkGroupRequest),
        }
      )
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(
              JSON.stringify({
                statusCode: response.status,
                statusText: response.statusText,
                body: await response.json(),
              })
            );
          }
          if (snackBarDetails) {
            snackBarDetails(`Work Group ${name} saved successfully`, true);
          }
          refetch();
          handleCancelClick();
        })
        .catch((error) => {
          if (error.message.indexOf("already exists") !== -1) {
            onError(`Unable to save work group ${name}. Group with same name already exists.`);
          } else if (
            error.message.indexOf(
              "Cannot delete template group associations. Templates require at least one group."
            ) != -1
          ) {
            onError("Template cannot be removed because this is the only work group.");
          } else {
            onError(`Unable to save work group ${name}. Please try again.`);
          }
          reportError(error.message);
        }));
  }

  const handleResourceChange = (
    _event: any,
    newValue: { key: string; name?: string; optionValue: string }[]
  ) => {
    setResources(
      newValue.filter((obj, index) => {
        return index === newValue.findIndex((o) => obj.key === o.key);
      })
    );
    setResourceInError(newValue.length == 0);
  };

  const handleTemplateChange = (
    _event: any,
    newValue: { key: string; name?: string; optionValue: string }[]
  ) => {
    setTemplates(
      newValue.filter((obj, index) => {
        return index === newValue.findIndex((o) => obj.key === o.key);
      })
    );
  };
  const isSaveDisabled = resourceInError || descInError || nameInError;
  return (
    <Paper
      elevation={0}
      sx={{
        pb: "20px",
      }}
    >
      <Typography
        variant="h6"
        sx={{ paddingTop: "20px", paddingBottom: "7px", paddingLeft: "3px" }}
      >
        {workGrp ? "Edit Group" : "Create Group"}
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          pb: "20px",
        }}
      >
        <Grid
          container
          sx={{
            marginX: { xs: 3, sm: 6.5 },
            paddingTop: "50px",
          }}
        >
          <Grid size={{ xs: 12, md: 6 }} sx={{ mb: "8px" }}>
            <TextField
              label="Name"
              variant="outlined"
              id="groupName"
              value={name}
              sx={{ width: "80%" }}
              error={nameInError}
              helperText={nameInError ? "Please enter name." : ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setName(event.target.value);
                setNameInError(!event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ mb: "16px" }}>
            <TextField
              label="Description"
              variant="outlined"
              id="groupDescription"
              value={description}
              sx={{ width: "80%" }}
              error={descInError}
              helperText={descInError ? "Please enter description." : ""}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDescription(event.target.value);
                setDescInError(!event.target.value);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} sx={{ mb: "16px" }}>
            <AutocompleteMultiSelectSearchBox
              id="templateSearch"
              name="templateSearch"
              width={"80%"}
              options={getTemplateOptions(templatesInTenant)}
              setFieldName={() => {}}
              placeholder="Templates"
              label="Template Search"
              value={templates}
              handleChange={handleTemplateChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Autocomplete
              id="peopleSearch"
              aria-label={"People Search Component"}
              multiple
              sx={{
                width: "80%",
                flexWrap: "wrap",
                "&.Mui-expanded": {
                  zIndex: 9999,
                  maxHeight: { xs: "100px", md: "180px" },
                  overflowY: "auto",
                },
              }}
              disableCloseOnSelect
              limitTags={4}
              options={getResourceOptions(resourcesInTenant)}
              getOptionLabel={(option) => option.optionValue}
              isOptionEqualToValue={(option, value) => option.key === value.key}
              value={resources}
              onChange={(_event, newValue) => handleResourceChange(_event, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="People Search"
                  placeholder="Select a person(s)"
                  error={resourceInError}
                  helperText={resourceInError ? "Please make a selection." : ""}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.key}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  {option.optionValue}
                </li>
              )}
            />
          </Grid>
          <Grid
            size={{ xs: 2.5 }}
            sx={{ mt: 2, display: "grid", marginLeft: "auto", justifyContent: "flex-end" }}
          >
            <Box
              sx={{
                bottom: "60px",
                display: "flex",
                flexDirection: "row",
                pt: 2,
                justifyContent: "center",
              }}
            >
              <Button
                onClick={handleSaveClick}
                variant="contained"
                color={"primary"}
                sx={{ focusRing, marginRight: "5px" }}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button
                onClick={() => handleCancelClick()}
                variant="outlined"
                sx={{ marginLeft: pxToRem(10), ...focusRing }}
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
